import type { Session, User } from "@supabase/supabase-js";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { getCookieValue } from "./helpers";
import { createBrowserClient } from "@supabase/ssr";
import { SUPABASE_ANON_KEY, SUPABASE_URL } from "./constants";
import { useIsShopsite } from "@context/DomainContext";
import { useThemeContext as radixUseThemeContext } from "@radix-ui/themes";

export function useWindowSize() {
  const [windowSize, setWindowSize] = useState<{
    width: number | undefined;
    height: number | undefined;
  }>({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      const handleResize = () => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      };

      window.addEventListener("resize", handleResize);

      handleResize();

      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);
  return windowSize;
}

export function useIsInViewport<T extends HTMLElement>() {
  const targetRef = useRef<T | null>(null);
  const [isInViewport, setIsInViewport] = useState(false);

  useEffect(() => {
    const observerRefValue: HTMLElement | null = targetRef.current;

    if (!observerRefValue) return;

    const observer = new IntersectionObserver(
      (entries) => {
        setIsInViewport(entries[0].isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(observerRefValue);

    return () => {
      if (observerRefValue) {
        observer.unobserve(observerRefValue);
      }
    };
  }, []);

  const scrollToTarget = useCallback(
    (block?: "nearest" | "start" | "end" | "center") => {
      if (targetRef.current) {
        targetRef.current.scrollIntoView({
          behavior: "smooth",
          block: block || "nearest",
        });
      }
    },
    [targetRef]
  );

  return { targetRef, scrollToTarget, isInViewport };
}

export function useInterval(callback: () => void, delay: number) {
  const savedCallback = useRef<() => void>(null);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    function tick() {
      if (savedCallback.current) {
        savedCallback.current();
      }
    }

    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export function useLatestCookieValue(
  cookieName: string,
  exact: boolean = true
) {
  const [latestCookie, setLatestCookie] = useState<string | undefined>(
    getCookieValue(cookieName, exact)
  );

  useEffect(() => {
    const checkCookie = () => {
      const cookieValue = getCookieValue(cookieName, exact);

      if (cookieValue !== latestCookie) {
        setLatestCookie(cookieValue);
      }
    };

    if (!latestCookie) {
      checkCookie();
    }

    const interval = setInterval(checkCookie, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [cookieName, latestCookie, exact]);

  return latestCookie;
}

export function usePrevious<T>(
  value: T,
  accept_empty = true
): RefObject<T | null> {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (accept_empty || value) {
      ref.current = value;
    }
  }, [value, accept_empty]);

  return ref;
}

export function useSupabaseUser() {
  const supabase = createBrowserClient(SUPABASE_URL!, SUPABASE_ANON_KEY!);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then((res) => setUser(res.data.user));
  }, [supabase]);

  return user;
}

export function useSupabaseSession(initialSession: Session | null = null) {
  const supabase = createBrowserClient(SUPABASE_URL!, SUPABASE_ANON_KEY!);
  // Seeding from a server-verified session (when the caller has one, e.g.
  // straight after a hard navigation where cookies already reflect the
  // signed-in user) avoids a render where this starts out null before
  // onAuthStateChange resolves asynchronously - that transient null was
  // observed to make callers briefly treat an authenticated user as a
  // guest. Only used as the initial value; onAuthStateChange below remains
  // the source of truth for every update after mount.
  const [session, setSession] = useState<Session | null>(initialSession);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_, session) => {
      setSession(session);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [supabase]);

  return session;
}

export function useHydrated() {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
  }, []);

  return hydrated;
}

export function useResizeObserver() {
  const ref = useRef<HTMLDivElement>(null);

  const [clientHeight, setClientHeight] = useState(0);
  const [clientWidth, setClientWidth] = useState(0);

  useEffect(() => {
    const { current } = ref;

    const observer = new ResizeObserver((entries) => {
      setClientHeight(entries[0].target.clientHeight);
      setClientWidth(entries[0].target.clientWidth);
    });
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current]);

  return { ref, clientHeight, clientWidth };
}

export function useThemeContext() {
  const isShopsite = useIsShopsite();

  if (isShopsite) {
    return null;
  }
  return radixUseThemeContext();
}
